import Vue from 'vue'
import Vuex from 'vuex'
import {
  createFolio,
  createOpeningFolio,
  publishFolioFromDraft,
  addFolioInteraction,
  finishFolio,
  getFolio,
  getFolioPdf,
  updateFolioVisibility,
  updateFolioMetadata,
  updateFolioDescription,
  acknowledgeFolio,
  listBooks,
  listAllFolios,
  listFolios,
} from '@/helpers/api/communicationBooks'
import { getProjectRequest, isCurrentProjectRequest } from '@/helpers/projectContext'
import { requestSignPin, signWithPin } from '@/helpers/api/signing'

Vue.use(Vuex)

const emptyListing = () => ({
  data: [],
  current_page: 1,
  last_page: 1,
  total: 0,
})

const initialState = () => ({
  books: [],
  selectedBookId: null,
  selectedBookFilterIds: [],
  folios: emptyListing(),
  currentFolio: null,
  draftToLoad: null,
  createFolioModal: false,
  createOpeningFolioModal: false,
  openingBookId: null,
  search: '',
  loadingBooks: false,
  loadingFolios: false,
  loadingFolio: false,
  acknowledgementLoading: false,
  acknowledgementError: null,
  visibilitySaving: false,
  visibilityError: null,
  metadataSaving: false,
  metadataError: null,
  descriptionSaving: false,
  descriptionError: null,
  saving: false,
  accessDenied: false,
  actionDenied: null,
  error: null,
})

const isAccessDenied = response => response && Number(response.code) === 403

// Las acciones de escritura denegadas avisan al usuario sin vaciar el módulo ni redirigir.
const denyAction = (context, response) => {
  context.commit('setActionDenied', {
    message: (response && response.message) || 'No tienes permisos para realizar esta acción.',
    key: Date.now(),
  })
  return null
}

const store = new Vuex.Store({
  state: initialState(),
  mutations: {
    setBooks(state, books) {
      state.books = books
      if (!books.some(book => book.id === state.selectedBookId)) {
        state.selectedBookId = books.length ? books[0].id : null
      }
    },
    setSelectedBookId(state, bookId) {
      state.selectedBookId = bookId
      state.folios = emptyListing()
    },
    setSelectedBookFilterIds(state, bookIds) {
      state.selectedBookFilterIds = Array.isArray(bookIds) ? bookIds : []
      state.folios = emptyListing()
    },
    setFolios(state, folios) {
      state.folios = Object.assign(emptyListing(), folios)
    },
    setCurrentFolio(state, folio) {
      state.currentFolio = folio
    },
    setDraftToLoad(state, draft) {
      state.draftToLoad = draft
    },
    setCreateFolioModal(state, status) {
      state.createFolioModal = status
    },
    setCreateOpeningFolioModal(state, status) {
      state.createOpeningFolioModal = status
    },
    setOpeningBookId(state, bookId) {
      state.openingBookId = bookId
    },
    setSearch(state, search) {
      state.search = search
    },
    setLoadingBooks(state, loading) {
      state.loadingBooks = loading
    },
    setLoadingFolios(state, loading) {
      state.loadingFolios = loading
    },
    setLoadingFolio(state, loading) {
      state.loadingFolio = loading
    },
    setAcknowledgementLoading(state, loading) {
      state.acknowledgementLoading = loading
    },
    setAcknowledgementError(state, error) {
      state.acknowledgementError = error
    },
    setVisibilitySaving(state, saving) {
      state.visibilitySaving = saving
    },
    setVisibilityError(state, error) {
      state.visibilityError = error
    },
    setMetadataSaving(state, saving) {
      state.metadataSaving = saving
    },
    setMetadataError(state, error) {
      state.metadataError = error
    },
    setDescriptionSaving(state, saving) {
      state.descriptionSaving = saving
    },
    setDescriptionError(state, error) {
      state.descriptionError = error
    },
    setSaving(state, saving) {
      state.saving = saving
    },
    setAccessDenied(state, accessDenied) {
      state.accessDenied = accessDenied
      if (accessDenied) {
        state.books = []
        state.selectedBookId = null
        state.folios = emptyListing()
        state.currentFolio = null
      }
    },
    setError(state, error) {
      state.error = error
    },
    setActionDenied(state, actionDenied) {
      state.actionDenied = actionDenied
    },
    resetForProject(state) {
      Object.assign(state, initialState())
    },
  },
  actions: {
    async loadBooks(context, projectId) {
      if (!projectId) return
      const request = getProjectRequest(projectId)
      context.commit('setLoadingBooks', true)
      context.commit('setError', null)

      try {
        const response = await listBooks(projectId)
        if (isCurrentProjectRequest(request.projectId, request.generation)) {
          if (isAccessDenied(response)) {
            context.commit('setAccessDenied', true)
            return
          }
          context.commit('setAccessDenied', false)
          context.commit('setBooks', Array.isArray(response) ? response : response.data || [])
          context.commit('setSelectedBookFilterIds', [])
        }
      } catch (error) {
        if (isCurrentProjectRequest(request.projectId, request.generation)) {
          context.commit('setError', error)
          context.commit('setBooks', [])
        }
      } finally {
        context.commit('setLoadingBooks', false)
      }
    },
    async loadFolios(context, { bookId, projectId, bookIds = [], priority = null, page = 1, search = context.state.search } = {}) {
      if (!bookId && !projectId) return
      context.commit('setLoadingFolios', true)
      context.commit('setSearch', search)
      context.commit('setError', null)

      try {
        const params = { page, per_page: 20, search }
        if (priority !== null && priority !== undefined && priority !== '') params.priority = priority
        let response
        if (projectId) {
          if (bookIds.length === 1) params.book_id = bookIds[0]
          if (bookIds.length > 1) params.book_ids = bookIds
          response = await listAllFolios(projectId, params)
        } else {
          response = await listFolios(bookId, params)
        }
        if (isAccessDenied(response)) {
          context.commit('setAccessDenied', true)
          return
        }
        context.commit('setAccessDenied', false)
        context.commit('setFolios', response || {})
      } catch (error) {
        context.commit('setError', error)
        context.commit('setFolios', emptyListing())
      } finally {
        context.commit('setLoadingFolios', false)
      }
    },
    async loadFolio(context, folioId) {
      context.commit('setLoadingFolio', true)
      context.commit('setError', null)
      try {
        const response = await getFolio(folioId)
        if (isAccessDenied(response)) {
          context.commit('setAccessDenied', true)
          return null
        }
        context.commit('setAccessDenied', false)
        context.commit('setCurrentFolio', response)
        return response
      } catch (error) {
        context.commit('setError', error)
        context.commit('setCurrentFolio', null)
        throw error
      } finally {
        context.commit('setLoadingFolio', false)
      }
    },
    async publishFolio(context, { bookId, data }) {
      context.commit('setSaving', true)
      context.commit('setError', null)
      try {
        const response = await createFolio(bookId, data)
        if (isAccessDenied(response)) return denyAction(context, response)
        context.commit('setCurrentFolio', response)
        await context.dispatch('loadFolios', { bookId, search: context.state.search })
        return response
      } catch (error) {
        context.commit('setError', error)
        throw error
      } finally {
        context.commit('setSaving', false)
      }
    },
    async openFolio(context, { bookId, projectId, data }) {
      context.commit('setSaving', true)
      context.commit('setError', null)
      try {
        const response = await createOpeningFolio(bookId, data)
        if (isAccessDenied(response)) return denyAction(context, response)
        context.commit('setCurrentFolio', response)
        if (projectId) await context.dispatch('loadBooks', projectId)
        await context.dispatch('loadFolios', { bookId, search: context.state.search })
        return response
      } catch (error) {
        context.commit('setError', error)
        throw error
      } finally {
        context.commit('setSaving', false)
      }
    },
    async publishDraft(context, { bookId, data }) {
      context.commit('setSaving', true)
      context.commit('setError', null)
      try {
        const response = await publishFolioFromDraft(bookId, data)
        if (isAccessDenied(response)) return denyAction(context, response)
        context.commit('setCurrentFolio', response)
        context.commit('setDraftToLoad', null)
        await context.dispatch('loadFolios', { bookId, search: context.state.search })
        return response
      } catch (error) {
        context.commit('setError', error)
        throw error
      } finally {
        context.commit('setSaving', false)
      }
    },
    async finishFolio(context, folioId) {
      context.commit('setError', null)
      const response = await finishFolio(folioId)
      if (isAccessDenied(response)) return denyAction(context, response)
      context.commit('setCurrentFolio', response)
      return response
    },
    async updateFolioVisibility(context, { folioId, visibility }) {
      context.commit('setVisibilitySaving', true)
      context.commit('setVisibilityError', null)
      try {
        const response = await updateFolioVisibility(folioId, visibility)
        if (isAccessDenied(response)) return denyAction(context, response)
        const currentFolio = context.state.currentFolio || {}
        context.commit('setCurrentFolio', Object.assign({}, currentFolio, response))
        return response
      } catch (error) {
        context.commit('setVisibilityError', error)
        throw error
      } finally {
        context.commit('setVisibilitySaving', false)
      }
    },
    async updateFolioMetadata(context, { folioId, data }) {
      context.commit('setMetadataSaving', true)
      context.commit('setMetadataError', null)
      try {
        const response = await updateFolioMetadata(folioId, data)
        if (isAccessDenied(response)) return denyAction(context, response)
        context.commit('setCurrentFolio', response)
        return response
      } catch (error) {
        context.commit('setMetadataError', error)
        throw error
      } finally {
        context.commit('setMetadataSaving', false)
      }
    },
    async updateFolioDescription(context, { folioId, description, idempotencyKey = null }) {
      context.commit('setDescriptionSaving', true)
      context.commit('setDescriptionError', null)
      try {
        const response = await updateFolioDescription(folioId, description, idempotencyKey)
        if (isAccessDenied(response)) return denyAction(context, response)
        context.commit('setCurrentFolio', response)
        return response
      } catch (error) {
        context.commit('setDescriptionError', error)
        throw error
      } finally {
        context.commit('setDescriptionSaving', false)
      }
    },
    async acknowledgeFolio(context, { folioId, idempotencyKey = null }) {
      context.commit('setAcknowledgementLoading', true)
      context.commit('setAcknowledgementError', null)
      try {
        const response = await acknowledgeFolio(folioId, idempotencyKey)
        if (isAccessDenied(response)) return denyAction(context, response)
        context.commit('setCurrentFolio', response)
        return response
      } catch (error) {
        context.commit('setAcknowledgementError', error)
        throw error
      } finally {
        context.commit('setAcknowledgementLoading', false)
      }
    },
    async requestFolioSignPin(context, folioId) {
      return requestSignPin({ signable_type: 'communication_folio', signable_id: folioId })
    },
    async signFolioWithPin(context, { folioId, pin }) {
      const response = await signWithPin({
        signable_type: 'communication_folio',
        signable_id: folioId,
        pin,
      })
      if (isAccessDenied(response)) return denyAction(context, response)
      return response
    },
    async addInteraction(context, { folioId, data, idempotencyKey = null }) {
      context.commit('setError', null)
      const response = idempotencyKey
        ? await addFolioInteraction(folioId, data, idempotencyKey)
        : await addFolioInteraction(folioId, data)
      if (isAccessDenied(response)) return denyAction(context, response)
      context.commit('setCurrentFolio', response)
      return response
    },
    async getFolioPdf(context, folioId) {
      return getFolioPdf(folioId)
    },
  },
})

export default store
